import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import BackendService from 'BackendService';
import FundUpdateBase from '../../pages/FundUpdateBase';
import FundInformation from '../FundUpdate/FundInformation';
import FundOpportunityEOI from '../../components/FundOpportunityEOI';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import useRecentlyViewed from '../../hooks/admin/useRecentlyViewed';
import FundOpportunity from '../../models/FundOpportunity';
import Investor, { investorToContactCardProps } from '../../models/Investor';
import InvestorContactCard from 'AdminModelCard';
import RecentlyViewedSidebar from 'RecentlyViewedSidebar';
import AdminInvestorExtraSection from 'FundUpdate/AdminInvestorExtraSection';
import { StyledHeading } from '../styles/styles';
import InvestorNotesSection from './InvestorNotesSection';
import StatementsSection from './FundOpportunityAdmin/StatementsSection';

interface FundUpdateWithInvestorProps {
  fundOpportunity: FundOpportunity;
}

export default function FundUpdateWithInvestor({ fundOpportunity }: FundUpdateWithInvestorProps) {
  const { investorId } = useParams<{ investorId: string }>();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as 'eoi' | 'fund-update' | null;
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [loading, setLoading] = useState(true);
  const { trackView } = useRecentlyViewed();
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentInvestor, setCurrentInvestor] = useState<Investor | null>(null);

  useEffect(() => {
    const fetchInvestor = async () => {
      if (!investorId) return;

      try {
        const data = await new BackendService().getInvestor(investorId);
        setInvestor(data);
        setCurrentInvestor(data);
        trackView('INVESTOR', investorId);
      } catch (err) {
        console.error('Failed to load investor', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestor();
  }, [investorId, trackView]);

  // Default tab: honour URL param first, then infer from investor status
  const defaultTab =
    investor?.status === 'LEAD' || investor?.status === 'OPPORTUNITY' ? 'eoi' : 'fund-update';
  const [activeTab, setActiveTab] = useState<'eoi' | 'fund-update'>(tabParam ?? defaultTab);
  const [activatedTabs, setActivatedTabs] = useState<Set<string>>(
    () => new Set([tabParam ?? defaultTab]),
  );

  // Once investor loads: apply tab param if present, otherwise infer from status
  useEffect(() => {
    if (investor) {
      const tab =
        tabParam ??
        (investor.status === 'LEAD' || investor.status === 'OPPORTUNITY' ? 'eoi' : 'fund-update');
      setActiveTab(tab);
      setActivatedTabs(new Set([tab]));
    }
  }, [investor, tabParam]);

  const handleTabChange = (_: React.SyntheticEvent, value: 'eoi' | 'fund-update') => {
    setActiveTab(value);
    setActivatedTabs((prev) => {
      const next = new Set(prev);
      next.add(value);
      return next;
    });
  };

  if (loading) {
    return (
      <Box mt={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!investor || !currentInvestor) {
    return (
      <Box mt={4} textAlign="center">
        Failed to load investor.
      </Box>
    );
  }

  const loadExistingDocs = async () =>
    new BackendService().getInvestorDocuments(currentInvestor.id);

  return (
    <>
      {/* Shared investor header */}
      <RecentlyViewedSidebar modelType="INVESTOR" limit={5} compact={true} />
      <StyledHeading headerVariant="subheading" component="div">
        Viewing for
        <InvestorContactCard
          {...investorToContactCardProps(currentInvestor, 'tone1')}
          viewLinks={{
            contact: `/contacts/${currentInvestor.contactId}`,
            fund: `/opportunities/${fundOpportunity.id}`,
          }}
        />
      </StyledHeading>
      <AdminInvestorExtraSection
        investor={currentInvestor}
        fundOpportunity={fundOpportunity}
        refreshSignal={refreshKey}
        onDocumentsAdded={() => setRefreshKey((k) => k + 1)}
        onInvestorUpdate={(updated) => setCurrentInvestor(updated)}
      />

      {/* Quick-capture notes — always visible above tabs for easy access during calls */}
      <InvestorNotesSection investorExternalId={currentInvestor.id} />

      <StatementsSection
        investorExternalId={currentInvestor.id}
        onLibraryChanged={() => setRefreshKey((k) => k + 1)}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mt: 2, mb: 1 }}>
        <Tab label="EOI" value="eoi" />
        <Tab label="Fund Update" value="fund-update" />
      </Tabs>

      {/* EOI tab — view-only, no Vera, no EOI submit section */}
      <Box role="tabpanel" data-testid="tab-panel-eoi" hidden={activeTab !== 'eoi'} sx={{ pt: 1 }}>
        {activatedTabs.has('eoi') ? (
          <FundOpportunityEOI
            key={refreshKey}
            fundOpportunity={fundOpportunity}
            hideVera={true}
            hideEoiSection={true}
          />
        ) : (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* Fund Update tab — full admin editing experience */}
      <Box
        role="tabpanel"
        data-testid="tab-panel-fund-update"
        hidden={activeTab !== 'fund-update'}
        sx={{ pt: 1 }}
      >
        {activatedTabs.has('fund-update') ? (
          <FundUpdateBase
            key={refreshKey}
            fundOpportunity={fundOpportunity}
            investor={currentInvestor}
            canEdit={true}
            parentTone="admin"
            backendService={() => new BackendService()}
            appendSection={
              <FundInformation
                investor={currentInvestor}
                fundOpportunity={fundOpportunity}
                loadDocs={loadExistingDocs}
              />
            }
          />
        ) : (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </>
  );
}
